/**
 * Page /admin/chorus — diagnostic & test de connexion Chorus Pro (super_admin).
 *
 * Deux blocs :
 *   1. État de la configuration : présence des secrets (sans les révéler), URLs,
 *      environnement détecté (sandbox/qualif vs production).
 *   2. Test de connexion « Ping » : appelle /api/admin/chorus/ping qui exécute la
 *      chaîne complète (token OAuth PISTE + compte technique + API rattachements).
 *      codeRetour: 0 = tout est bon. En mode mock (ENABLED=false), renvoie des
 *      données factices — utile pour valider le câblage avant d'avoir les secrets.
 */

import { useEffect, useState } from 'react';
import { Card, Badge, Alert, Button, SectionEyebrow } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Plug, CheckCircle2, XCircle, Loader2, AlertCircle, Server, KeyRound } from 'lucide-react';

type ConfigResp = Awaited<ReturnType<typeof api.admin.chorus.config>>;
type PingResp = {
  ok: boolean;
  source: 'mock' | 'reel';
  codeRetour: number;
  libelle: string;
  structures: Array<{ idStructure: number; identifiantStructure: string; typeIdentifiantStructure: string; designationStructure: string; statutUtilisateur: string }>;
};

export function ChorusDiagnosticPage() {
  const { tenant } = useAuth();
  const [config, setConfig] = useState<ConfigResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [ping, setPing] = useState<PingResp | null>(null);
  const [pinging, setPinging] = useState(false);
  const [pingError, setPingError] = useState<string | null>(null);

  async function chargerConfig() {
    setLoading(true); setError(null);
    try { setConfig(await api.admin.chorus.config()); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { chargerConfig(); }, []);

  async function lancerPing() {
    setPinging(true); setPingError(null); setPing(null);
    try {
      // Test avec le compte technique de la commune courante (fonctionne en
      // hotline : le super_admin agit avec le tenant de la mairie).
      if (!tenant?.id) { setPingError('Aucune commune active. Entrez en hotline sur une mairie pour tester son raccordement.'); return; }
      const r = await api.admin.chorus.pingTenant(tenant.id);
      if (!r.ok && r.message) { setPingError(r.message); return; }
      setPing({
        ok: !!r.ok,
        source: r.source ?? 'reel',
        codeRetour: r.codeRetour ?? -1,
        libelle: r.libelle ?? '',
        structures: r.structures ?? [],
      });
    } catch (e) { setPingError(e instanceof ApiException ? e.message : 'Échec du test'); }
    finally { setPinging(false); }
  }

  return (
    <div className="space-y-6">
      <div>
        <SectionEyebrow as="div">Intégration</SectionEyebrow>
        <h1 className="mt-1 text-2xl font-semibold text-ink">Chorus Pro — diagnostic</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Vérifie le raccordement API Chorus Pro (OAuth PISTE + compte technique) et teste la connexion de bout en bout.
        </p>
      </div>

      {error && <Alert variant="error"><AlertCircle className="h-4 w-4" /> {error}</Alert>}

      {loading ? (
        <Card><div className="flex items-center gap-2 p-6 text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div></Card>
      ) : config && (
        <>
          {/* Bloc 1 — Configuration */}
          <Card>
            <div className="border-b border-border p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-ink"><Server className="h-4 w-4" /> Configuration</div>
            </div>
            <div className="space-y-3 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={config.actif ? 'success' : 'neutral'}>
                  {config.actif ? 'Actif (appels réels)' : 'Mock (CHORUS_PRO_ENABLED=false)'}
                </Badge>
                <Badge variant={config.environnement.includes('sandbox') ? 'info' : 'urgent'}>
                  {config.environnement}
                </Badge>
              </div>

              <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div><dt className="text-ink-subtle">URL API</dt><dd className="font-mono text-xs text-ink">{config.baseUrl}</dd></div>
                <div><dt className="text-ink-subtle">URL OAuth</dt><dd className="font-mono text-xs text-ink">{config.oauthUrl}</dd></div>
              </dl>

              <div>
                <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-ink-subtle"><KeyRound className="h-3.5 w-3.5" /> Raccordement OFAD (global)</div>
                <div className="flex flex-wrap gap-2">
                  <SecretPill label="Client ID" ok={config.secretsPresents.clientId} />
                  <SecretPill label="Client Secret" ok={config.secretsPresents.clientSecret} />
                </div>
                <p className="mt-2 text-xs text-ink-subtle">
                  Le compte technique n'est plus une variable globale : il se saisit
                  par commune dans la fiche mairie (Mairie → Chorus Pro direct).
                  Utilisez « Tester la connexion » ci-dessous pour valider la commune.
                </p>
              </div>

              {config.configManquante.length > 0 && (
                <Alert variant="warning">
                  <AlertCircle className="h-4 w-4" />
                  <div>
                    <div className="font-medium">Raccordement OFAD incomplet</div>
                    <div className="text-xs">À vérifier dans Render : {config.configManquante.join(', ')}. (Client OAuth PISTE et activation — le compte technique, lui, se saisit par commune dans la fiche mairie.)</div>
                  </div>
                </Alert>
              )}
            </div>
          </Card>

          {/* Bloc 2 — Test de connexion */}
          <Card>
            <div className="border-b border-border p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-ink"><Plug className="h-4 w-4" /> Test de connexion</div>
                <Button variant="primary" onClick={lancerPing} disabled={pinging}>
                  {pinging ? <><Loader2 className="h-4 w-4 animate-spin" /> Test en cours…</> : 'Tester la connexion'}
                </Button>
              </div>
            </div>
            <div className="space-y-3 p-4">
              <p className="text-xs text-ink-muted">
                Récupère les structures rattachées au compte technique. C'est un test de bout en bout :
                token OAuth PISTE, compte technique, et accès API. <strong>codeRetour&nbsp;0 = tout fonctionne.</strong>
                {!config.actif && ' (Mode mock : renvoie des données factices tant que CHORUS_PRO_ENABLED=false.)'}
              </p>

              {pingError && <Alert variant="error"><XCircle className="h-4 w-4" /> {pingError}</Alert>}

              {ping && (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {ping.ok
                      ? <Badge variant="success"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Connexion OK (codeRetour {ping.codeRetour})</Badge>
                      : <Badge variant="urgent"><XCircle className="mr-1 h-3.5 w-3.5" /> Échec (codeRetour {ping.codeRetour})</Badge>}
                    <Badge variant={ping.source === 'reel' ? 'info' : 'neutral'}>{ping.source === 'reel' ? 'Appel réel' : 'Mock'}</Badge>
                    <span className="font-mono text-xs text-ink-subtle">{ping.libelle}</span>
                  </div>

                  {ping.structures.length > 0 && (
                    <div className="overflow-hidden rounded-lg border border-border">
                      <table className="w-full text-sm">
                        <thead className="bg-surface/60 text-left text-xs text-ink-subtle">
                          <tr>
                            <th className="p-2 font-medium">Structure</th>
                            <th className="p-2 font-medium">SIRET</th>
                            <th className="p-2 font-medium">Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ping.structures.map((s) => (
                            <tr key={s.idStructure} className="border-t border-border">
                              <td className="p-2 text-ink">{s.designationStructure}</td>
                              <td className="p-2 font-mono text-xs text-ink-muted">{s.identifiantStructure}</td>
                              <td className="p-2"><Badge variant={s.statutUtilisateur === 'ACTIF' ? 'success' : 'neutral'}>{s.statutUtilisateur}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function SecretPill({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className={['inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
      ok ? 'bg-success/10 text-success' : 'bg-ink/5 text-ink-subtle'].join(' ')}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />} {label}
    </span>
  );
}
